import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { navigationService } from './services/navigation';

import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Bookings from './pages/Bookings';
import NewBooking from './pages/NewBooking';
import Contact from './pages/Contact';
import UserProfile from './components/UserProfile';
import AdminDashboard from './pages/AdminDashboard';
import OAuthCallback from './pages/OAuthCallback';
import VendorDashboard from './pages/VendorDashboard';
import DriverDashboard from './pages/DriverDashboard';
import VendorRegister from './pages/VendorRegister';
import DriverRegister from './pages/DriverRegister';
import ServiceProviderPage from './pages/ServiceProvider';
import { ThemeProvider } from './premium/theme/ThemeProvider';

const PremiumApp = React.lazy(() => import('./premium/PremiumApp'));
const PremiumLoginPage = React.lazy(() => import('./premium/pages/LoginPage'));
const PremiumRegisterPage = React.lazy(() => import('./premium/pages/RegisterPage'));

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Vendor Route Component
const VendorRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'vendor') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Driver Route Component
const DriverRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'driver') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Navigation initializer component
const NavigationInitializer: React.FC = () => {
  const navigate = useNavigate();
  
  React.useEffect(() => {
    navigationService.setNavigate(navigate);
  }, [navigate]);
  
  return null;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ThemeProvider>
        <Router>
          <NavigationInitializer />
          <Layout>
            <Suspense
              fallback={
                <div className="min-vh-100 d-flex align-items-center justify-content-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/vendor-register" element={<VendorRegister />} />
                <Route path="/driver-register" element={<DriverRegister />} />
                <Route path="/oauth-callback" element={<OAuthCallback />} />
                <Route path="/services" element={<Navigate to="/bookings/new" replace />} />
                <Route path="/contact" element={<Contact />} />
                <Route 
                  path="/bookings" 
                  element={
                    <ProtectedRoute>
                      <Bookings />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/bookings/new" element={<NewBooking />} />
                <Route 
                  path="/profile" 
                  element={
                    <ProtectedRoute>
                      <UserProfile />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  } 
                />
                <Route 
                  path="/vendor-dashboard" 
                  element={
                    <VendorRoute>
                      <VendorDashboard />
                    </VendorRoute>
                  } 
                />
                <Route 
                  path="/driver-dashboard" 
                  element={
                    <DriverRoute>
                      <DriverDashboard />
                    </DriverRoute>
                  } 
                />
                <Route path="/provider" element={<ServiceProviderPage />} />
                <Route path="/premium/login" element={<PremiumLoginPage />} />
                <Route path="/premium/register" element={<PremiumRegisterPage />} />
                <Route
                  path="/premium/*"
                  element={
                    <ProtectedRoute>
                      <PremiumApp />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
          </Layout>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
};

export default App;
